// File: src/features/ManageCMDBDiscoveredAppsSchedule/components/SchedulingFrequencySelector.tsx
import React from 'react';
import { Box, Typography } from '@saviynt/design-system';

import testIds from '../../../../constants/testIds';
import useLocalization from '../../../../hooks/use-localize';
import SchedulingIntervalInput from '../SchedulingIntervalInput/SchedulingIntervalInput';

import './SchedulingFrequencySelector.css';

interface SchedulingFrequencySelectorProps {
  disabled?: boolean;
  interval: number;
  onIntervalChange: (value: number) => void;
  onEmptyChange?: (isEmpty: boolean) => void;
  dataTestId?: string;
  timeLabel: string;
}

/**
 * Frequency selector with interval input and unit display.
 */
function SchedulingFrequencySelector({
  disabled = false,
  interval,
  onIntervalChange,
  onEmptyChange = undefined,
  dataTestId,
  timeLabel,
}: SchedulingFrequencySelectorProps) {
  const t = useLocalization();
  const unitLabel = t('INTEGRATIONS.SCHEDULING_FREQUENCY.UNIT.WEEK');
  const heading = t('INTEGRATIONS.LABEL.DISCOVERY_FREQUENCY');

  return (
    <section
      className='scheduling-frequency'
      data-testid={dataTestId || testIds.SCHEDULING_FREQUENCY_SELECTOR}
      aria-labelledby='discovery-frequency-heading'>
      <Typography kind='h4' color='--color-foreground-neutral-bold'>
        {heading}
      </Typography>

      <div className='scheduling-frequency-body'>
        <Typography
          htmlFor='repeatEvery'
          kind='body2'
          className='scheduling-frequency-body-label'>
          {t('INTEGRATIONS.SCHEDULING_FREQUENCY.LABEL.REPEAT_EVERY')}
        </Typography>

        <Box className='scheduling-frequency-body-value'>
          <SchedulingIntervalInput
            disabled={disabled}
            value={interval}
            onChange={onIntervalChange}
            onEmptyChange={onEmptyChange}
            dataTestId={testIds.SCHEDULING_INTERVAL_INPUT}
          />
        </Box>

        <Typography kind='body2'>
          {unitLabel}{' '}
          {`${t('INTEGRATIONS.SCHEDULING.REPEATS_AT', `at ${timeLabel} UTC.`, {
            timeLabel,
          })}`}
        </Typography>
      </div>
    </section>
  );
}

SchedulingFrequencySelector.defaultProps = {
  disabled: false,
  onEmptyChange: undefined,
  dataTestId: undefined,
};

export default React.memo(SchedulingFrequencySelector);
